import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { DimensionInputUnit } from 'ui-components/components/dimension-input/units';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { WeightInput } from 'ui-components/components/weight-input';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CSWFieldId } from '../../../../../../../constants/field-ids';
import { PackageContentDescriptionLabel } from '../../../../../../../fulfillment-planner/package-content-description/label';
import { Scale } from '../../../../../../../fulfillment-planner/weight/scale';
import { IntlUnitFormattingService } from '../../../../../../../services/intl-unit-formatting-services/intl-unit-formatting-service';
import { toFixedNumber } from '../../../../../../../services/number-formatting-service';
import { convertFromDimensionUnitEnum } from '../../../../../../../services/unit-services/dimensions/dimension-unit-enum-converter';
import {
  convertFromWeightUnitEnum,
  convertToWeightUnitEnum,
} from '../../../../../../../services/unit-services/weights/weight-unit-enum-converter';
import { PackageContentDescriptionInputForMultiPackage } from '../../../../../multi-package-modal/package-content-description/input';
import { PackageInsurance } from '../../../../../multi-package-modal/package-insurance';
import { useMultiPackageModalContext } from '../../../../contexts/multi-package-modal-context';
import { CustomPackageList } from '../../../custom-package-list/custom-package-list';
import { messages } from './package-details-section-messages';
import style from './package-details-section.module.css';

import type { Package } from '../../../../types';
import type { Dimensions, Weight } from '../../../../../multi-package-modal/package-set';

type PackageDetailsSectionProps = {
  pack: Package;
  packageIndex: number;
  elementId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onReadFromScale: (result: { weight: Weight; dims?: Dimensions }) => void;
};

export const PackageDetailsSection = ({
  pack,
  packageIndex,
  elementId,
  isExpanded,
  onToggleExpand,
  onReadFromScale,
}: PackageDetailsSectionProps) => {
  const { formatMessage } = useIntl();
  const {
    customPackages,
    hasInsuranceProviderSelected,
    shouldRequirePackageDescription,
    changePackageDimensions,
    changeWeight,
    changePackageContentDescription,
    changeInsuranceValue,
    applyCustomPackageToPackage,
  } = useMultiPackageModalContext();

  const hasCustomPackages = customPackages.length > 0;

  if (!isExpanded) {
    const dimensionUnitSymbol =
      convertFromDimensionUnitEnum(pack.dimensions?.unit) === DimensionInputUnit.centimeters
        ? formatMessage(messages.unitCentimeters)
        : formatMessage(messages.unitInches);

    return (
      <FlexRow className={style['package-summary']}>
        <FlexRow gap={Gap.Space8}>
          <Text
            textType={TextType.Caption}
            value={messages.packageDimensions}
            formattedMessageValues={{
              length: pack.dimensions.length,
              width: pack.dimensions.width,
              height: pack.dimensions.height,
              unit: dimensionUnitSymbol,
            }}
          />
          <Text
            textType={TextType.Caption}
            value={messages.packageWeight}
            formattedMessageValues={{
              weight: pack.weight?.value ? IntlUnitFormattingService.formatWeight(pack.weight, pack.weight.unit) : '--',
            }}
          />
        </FlexRow>
        <Button className={style['package-edit-button']} theme={THEME_CONSTANTS.ghost} onClick={onToggleExpand}>
          <FontAwesomeIcon icon={faPen} />
          <FormattedMessage {...messages.editPackage} />
        </Button>
      </FlexRow>
    );
  }

  return (
    <FlexColumn className={style['package-edit-form']}>
      <div>
        <FlexRow className={style['dimensions-header']}>
          <Text textType={TextType.Label} value={messages.sizeLabel} />
          {hasCustomPackages && (
            <CustomPackageList
              customPackages={customPackages}
              applyCustomPackage={(customPackage) => applyCustomPackageToPackage(customPackage, packageIndex)}
            />
          )}
        </FlexRow>
        <DimensionInput
          dimensions={pack.dimensions}
          labelId={`${elementId}${CSWFieldId.Dimension}`}
          onChange={(dimensions) => changePackageDimensions(dimensions, packageIndex)}
          unit={convertFromDimensionUnitEnum(pack.dimensions?.unit)}
        />
      </div>

      <FlexColumn>
        <Text textType={TextType.Label} value={messages.weightLabel} />
        <FlexRow gap={Gap.Space8}>
          <WeightInput
            className={style['package-weight-input']}
            labelId={`${elementId}${CSWFieldId.Weight}`}
            onChange={(weight: number, unit: string) => {
              const weightData: Weight = {
                unit: convertToWeightUnitEnum(unit),
                value: weight,
              };
              changeWeight(weightData, packageIndex);
            }}
            unit={convertFromWeightUnitEnum(pack.weight?.unit)}
            weight={toFixedNumber(pack.weight?.value ?? '0', 3)}
          />
          <Scale className={style.scale} onReadFromScale={onReadFromScale} buttonText={formatMessage(messages.scaleLabel)} />
        </FlexRow>
      </FlexColumn>

      {shouldRequirePackageDescription && (
        <div>
          <PackageContentDescriptionLabel />
          <PackageContentDescriptionInputForMultiPackage
            labelId={`${elementId}${CSWFieldId.PackageDescription}`}
            initialValue={pack.contentDescription ?? ''}
            onBlur={(description) => changePackageContentDescription(description, packageIndex)}
          />
        </div>
      )}

      <FlexColumn>
        <Text textType={TextType.Label} value={messages.insuranceLabel} />
        <FlexRow className={style['insured-amount-row']}>
          <PackageInsurance
            hasInsuranceProviderSelected={hasInsuranceProviderSelected}
            insuredValue={pack.insuredValue}
            labelId={`${elementId}${CSWFieldId.InsuranceAmount}`}
            onChange={(amount, currencyCode) => changeInsuranceValue(amount, currencyCode, packageIndex)}
          />
          <Button onClick={onToggleExpand} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...messages.savePackage} />
          </Button>
        </FlexRow>
      </FlexColumn>
    </FlexColumn>
  );
};
